/**
 * PilotFeedbackPage — technician feedback capture and explicit priority triage
 * (FULL-0164 / PROD-UX-078; MASTER_FLOW §14C.6).
 *
 * Two operations only:
 *
 *   1. capture a real pilot observation — severity, workflow, reproducibility, the §14C.6 UX
 *      signal and pointers to ticket/run/screen evidence (never customer content or credentials);
 *   2. an Admin/Owner explicitly classifies it as a resolved DEV fix (with a completed task or
 *      commit reference) or a manual/pilot blocker (with a reason and an owner).
 *
 * Truthfulness rules this surface keeps:
 *   - an empty store renders an explicit empty state; there is no seed, sample or invented item;
 *   - a failed list read is "unknown", never zero findings;
 *   - a high-impact item (severity blocker/high) is marked "awaiting explicit triage"; it is
 *     never auto-classified, auto-prioritized or turned into a task here;
 *   - capture never sets status/triage/actor — the server reads the actor from the session and
 *     only `apply_triage` writes a classification;
 *   - an unreadable stored row is reported, not hidden;
 *   - this page grants no permission, approval, execution or risk authority.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../context/AuthContext";
import { pilotFeedbackApi } from "../services/api";
import { detailText } from "../utils/apiErrors";
import { parseUtc } from "../utils/utcDate";
import { FOCUS_RING_CLS, TOUCH_TARGET_CLS } from "../components/designSystem/designTokens";
import {
  REPRODUCIBILITIES,
  SECRET_REFUSAL,
  SEVERITIES,
  TRIAGE_OUTCOMES,
  UX_SIGNALS,
  WORKFLOWS,
  isHighImpact,
  looksLikeSecret,
  outcomeMeta,
  reproducibilityLabel,
  severityMeta,
  uxSignalLabel,
  workflowLabel,
} from "../components/pilotFeedbackVocabulary";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const APP_PATH_RE = /^\/[A-Za-z0-9/_-]*$/;
const TASK_OR_COMMIT_RE = /^(?:[A-Z][A-Z0-9]*(?:-[A-Z0-9]+)*-[0-9]{1,6}|[0-9a-f]{7,40})$/;

const EMPTY_FORM = Object.freeze({
  severity: "",
  workflow: "",
  reproducibility: "",
  ux_signal: "",
  title: "",
  observed: "",
  expected: "",
  ticket_id: "",
  run_id: "",
  app_path: "",
});

function newRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const value = Math.floor(Math.random() * 16);
    return (char === "x" ? value : (value & 0x3) | 0x8).toString(16);
  });
}

function formatMoment(value) {
  if (!value) return "—";
  const date = parseUtc(value);
  if (Number.isNaN(date.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" }).format(date);
  } catch {
    return "—";
  }
}

function actorName(actor) {
  return actor?.display_name || "—";
}

export function validateCapture(form) {
  if (!SEVERITIES.includes(form.severity)) return "בחרו חומרת פידבק.";
  if (!WORKFLOWS.includes(form.workflow)) return "בחרו את זרימת העבודה שבה נתקלתם בבעיה.";
  if (!REPRODUCIBILITIES.includes(form.reproducibility)) return "בחרו את מידת השחזור.";
  if (!UX_SIGNALS.includes(form.ux_signal)) return "בחרו את סוג איתות חוויית המשתמש.";

  const title = form.title.trim();
  if (title.length < 5) return "הכותרת קצרה מדי (5 תווים לפחות).";
  if (title.length > 140) return "הכותרת ארוכה מדי (עד 140 תווים).";

  const observed = form.observed.trim();
  if (observed.length < 10) return "תיאור האירוע קצר מדי (10 תווים לפחות).";
  if (observed.length > 2000) return "תיאור האירוע ארוך מדי (עד 2,000 תווים).";

  if (form.expected.trim().length > 1000) return "הציפייה ארוכה מדי (עד 1,000 תווים).";
  if (looksLikeSecret([title, observed, form.expected].join("\n"))) return SECRET_REFUSAL;

  const ticket = form.ticket_id.trim();
  if (ticket && !UUID_RE.test(ticket)) return "מזהה הקריאה המקושרת חייב להיות UUID.";
  const run = form.run_id.trim();
  if (run && !UUID_RE.test(run)) return "מזהה הריצה המקושרת חייב להיות UUID.";

  const path = form.app_path.trim();
  if (path && (!APP_PATH_RE.test(path) || path.includes("//") || path.includes(".."))) {
    return "קישור הראיה חייב להיות נתיב פנימי במערכת, בלי כתובת חיצונית, שאילתה או מעבר תיקיות.";
  }
  return null;
}

export function validateTriage(outcome, values) {
  if (!TRIAGE_OUTCOMES.includes(outcome)) return "בחרו תוצאת סיווג מפורשת.";
  if (outcome === "dev_fix") {
    const reference = (values.resolution_ref || "").trim();
    if (!TASK_OR_COMMIT_RE.test(reference)) {
      return "תיקון פיתוח חייב להפנות למשימה שהושלמה (למשל FULL-0164) או ל-commit אמיתי.";
    }
    if (looksLikeSecret(values.resolution_note || "")) return SECRET_REFUSAL;
    return null;
  }
  const reason = (values.blocker_reason || "").trim();
  if (reason.length < 10) return "פרטו את סיבת החסימה (10 תווים לפחות).";
  if (looksLikeSecret(reason)) return SECRET_REFUSAL;
  if (!(values.owner || "").trim()) return "ציינו בעלים לחסימה.";
  if (looksLikeSecret(values.owner || "")) return SECRET_REFUSAL;
  return null;
}

function EvidenceLinks({ evidence }) {
  const ticketId = evidence?.ticket_id;
  const runId = evidence?.run_id;
  const appPath = evidence?.app_path;
  if (!ticketId && !runId && !appPath) {
    return <p className="text-xs text-gray-500">לא צורפו קישורי ראיה.</p>;
  }
  return (
    <ul className="flex flex-wrap gap-3 text-xs" aria-label="קישורי ראיה">
      {ticketId && (
        <li>
          <Link className="text-brand-700 underline" to={`/tickets/${ticketId}`}>
            פתיחת הקריאה המקושרת
          </Link>
        </li>
      )}
      {runId && (
        <li>
          <Link className="text-brand-700 underline" to={`/agent-runs/${runId}`}>
            פתיחת הריצה המקושרת
          </Link>
        </li>
      )}
      {appPath && (
        <li>
          <Link className="text-brand-700 underline" to={appPath}>
            פתיחת מסך המקור
          </Link>
        </li>
      )}
    </ul>
  );
}

function TriageForm({ item }) {
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState("");
  const [resolutionRef, setResolutionRef] = useState("");
  const [resolutionNote, setResolutionNote] = useState("");
  const [blockerReason, setBlockerReason] = useState("");
  const [owner, setOwner] = useState("");
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: (payload) => pilotFeedbackApi.triage(item.id, payload),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ["pilot-feedback"] });
    },
    onError: (failure) => {
      setSaved(false);
      setError(detailText(failure?.response?.data?.detail, "שמירת הסיווג נכשלה."));
    },
  });

  const submit = (event) => {
    event.preventDefault();
    setSaved(false);
    const values = {
      resolution_ref: resolutionRef,
      resolution_note: resolutionNote,
      blocker_reason: blockerReason,
      owner,
    };
    const refusal = validateTriage(outcome, values);
    if (refusal) {
      setError(refusal);
      return;
    }
    setError(null);
    mutation.mutate({
      outcome,
      resolution_ref: outcome === "dev_fix" ? resolutionRef.trim() : null,
      resolution_note: outcome === "dev_fix" ? resolutionNote.trim() || null : null,
      blocker_reason: outcome === "pilot_blocker" ? blockerReason.trim() : null,
      owner: outcome === "pilot_blocker" ? owner.trim() : null,
    });
  };

  const prefix = `triage-${item.id}`;
  const form = (
    <form className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3" onSubmit={submit} noValidate>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={`${prefix}-outcome`} className="block text-xs font-medium text-gray-700">
            תוצאת סיווג
          </label>
          <select
            id={`${prefix}-outcome`}
            className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm`}
            value={outcome}
            onChange={(event) => setOutcome(event.target.value)}
          >
            <option value="">בחרו תוצאה…</option>
            {TRIAGE_OUTCOMES.map((value) => (
              <option key={value} value={value}>
                {outcomeMeta(value).label}
              </option>
            ))}
          </select>
        </div>

        {outcome === "dev_fix" && (
          <>
            <div>
              <label htmlFor={`${prefix}-reference`} className="block text-xs font-medium text-gray-700">
                אסמכתת תיקון (משימה או commit)
              </label>
              <input
                id={`${prefix}-reference`}
                type="text"
                dir="ltr"
                placeholder="FULL-0164"
                className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm`}
                value={resolutionRef}
                onChange={(event) => setResolutionRef(event.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor={`${prefix}-note`} className="block text-xs font-medium text-gray-700">
                הערת תיקון (אופציונלי)
              </label>
              <input
                id={`${prefix}-note`}
                type="text"
                className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm`}
                value={resolutionNote}
                onChange={(event) => setResolutionNote(event.target.value)}
              />
            </div>
          </>
        )}

        {outcome === "pilot_blocker" && (
          <>
            <div className="sm:col-span-2">
              <label htmlFor={`${prefix}-reason`} className="block text-xs font-medium text-gray-700">
                סיבת החסימה
              </label>
              <textarea
                id={`${prefix}-reason`}
                rows={2}
                className={`${FOCUS_RING_CLS} mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm`}
                value={blockerReason}
                onChange={(event) => setBlockerReason(event.target.value)}
              />
            </div>
            <div>
              <label htmlFor={`${prefix}-owner`} className="block text-xs font-medium text-gray-700">
                בעלים
              </label>
              <input
                id={`${prefix}-owner`}
                type="text"
                className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm`}
                value={owner}
                onChange={(event) => setOwner(event.target.value)}
              />
            </div>
          </>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-2 text-xs text-red-700" data-testid={`${prefix}-error`}>
          {error}
        </p>
      )}
      {saved && (
        <p role="status" className="mt-2 text-xs text-green-700" data-testid={`${prefix}-saved`}>
          הסיווג נשמר.
        </p>
      )}

      <button
        type="submit"
        disabled={mutation.isPending}
        className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} mt-3 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-60`}
      >
        {mutation.isPending ? "שומר…" : "שמירת סיווג"}
      </button>
    </form>
  );

  if (item.triage) {
    return (
      <details className="mt-3">
        <summary className="cursor-pointer text-xs font-medium text-gray-600">סיווג מחדש</summary>
        {form}
      </details>
    );
  }
  return form;
}

function FeedbackCard({ item, canTriage }) {
  const severity = severityMeta(item.severity);
  const highImpact = isHighImpact(item.severity);
  const outcome = item.triage?.outcome || "unclassified";
  const outcomeInfo = outcomeMeta(outcome);

  return (
    <article
      className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
      data-testid={`pilot-feedback-${item.id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${severity.className}`}>
          חומרה: {severity.label}
        </span>
        {highImpact && (
          <span
            className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800"
            data-testid="high-impact-flag"
          >
            השפעה גבוהה
          </span>
        )}
        <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${outcomeInfo.className}`} data-testid="triage-status">
          {outcomeInfo.label}
        </span>
      </div>

      <h3 className="mt-2 text-base font-semibold text-gray-900">{item.title}</h3>
      <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs text-gray-600 sm:grid-cols-2">
        <div className="flex gap-1">
          <dt className="font-medium">זרימת עבודה:</dt>
          <dd>{workflowLabel(item.workflow)}</dd>
        </div>
        <div className="flex gap-1">
          <dt className="font-medium">שחזור:</dt>
          <dd>{reproducibilityLabel(item.reproducibility)}</dd>
        </div>
        <div className="flex gap-1 sm:col-span-2">
          <dt className="font-medium">איתות UX:</dt>
          <dd>{uxSignalLabel(item.ux_signal)}</dd>
        </div>
        <div className="flex gap-1 sm:col-span-2">
          <dt className="font-medium">נרשם:</dt>
          <dd>
            {actorName(item.created_by)} · {formatMoment(item.created_at)}
          </dd>
        </div>
      </dl>

      <div className="mt-3 space-y-2 text-sm text-gray-800">
        <p className="whitespace-pre-wrap break-words">{item.observed}</p>
        {item.expected && (
          <p className="whitespace-pre-wrap break-words text-gray-600">
            <span className="font-medium">ציפייה: </span>
            {item.expected}
          </p>
        )}
      </div>

      <div className="mt-3">
        <EvidenceLinks evidence={item.evidence} />
      </div>

      {item.triage ? (
        <div className="mt-3 rounded-lg bg-gray-50 p-3 text-xs text-gray-700" data-testid="triage-record">
          {item.triage.outcome === "dev_fix" ? (
            <p>
              <span className="font-medium">אסמכתת תיקון: </span>
              <span dir="ltr">{item.triage.resolution_ref || "—"}</span>
              {item.triage.resolution_note ? ` — ${item.triage.resolution_note}` : ""}
            </p>
          ) : (
            <p>
              <span className="font-medium">סיבת חסימה: </span>
              {item.triage.blocker_reason || "—"}
              <span className="font-medium"> · בעלים: </span>
              {item.triage.owner || "—"}
            </p>
          )}
          <p className="mt-1">
            סווג על ידי {actorName(item.triage.triaged_by)} · {formatMoment(item.triage.triaged_at)}
          </p>
        </div>
      ) : highImpact ? (
        <p className="mt-3 text-xs font-medium text-amber-800" role="status">
          השפעה גבוהה — ממתין לסיווג מפורש של מנהל או בעלים. אין סיווג אוטומטי.
        </p>
      ) : (
        <p className="mt-3 text-xs text-gray-600">טרם סווג.</p>
      )}

      {canTriage ? (
        <TriageForm item={item} />
      ) : (
        !item.triage && (
          <p className="mt-2 text-xs text-gray-500">סיווג נעשה על ידי מנהל או בעלים; אין כאן הרחבת הרשאה.</p>
        )
      )}
    </article>
  );
}

function Summary({ data }) {
  const summary = data.summary;
  const tiles = [
    { key: "total", label: "רשומות שנרשמו", value: summary.total, testId: "summary-total" },
    { key: "high", label: "השפעה גבוהה", value: summary.high_impact, testId: "summary-high-impact" },
    {
      key: "high-unclassified",
      label: "השפעה גבוהה שטרם סווגה",
      value: summary.high_impact_unclassified,
      testId: "summary-high-impact-unclassified",
    },
    { key: "dev", label: "תוקן בפיתוח", value: summary.dev_fix, testId: "summary-dev-fix" },
    { key: "blocker", label: "חוסמים ידניים/פיילוט", value: summary.pilot_blocker, testId: "summary-pilot-blocker" },
  ];
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {tiles.map((tile) => (
          <div key={tile.key} className="rounded-xl border border-gray-200 bg-white p-3">
            <p className="text-xs text-gray-500">{tile.label}</p>
            <p className="mt-1 text-2xl font-bold text-gray-900" data-testid={tile.testId}>
              {tile.value}
            </p>
          </div>
        ))}
      </div>
      {data.unreadable > 0 && (
        <p role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
          {data.unreadable} רשומות שמורות אינן קריאות. הן אינן נמחקות ואינן נספרות בסיכום.
        </p>
      )}
      {data.truncated && (
        <p className="rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-700">
          מוצגות {data.returned} רשומות מתוך {data.total_stored} שמורות. הסיכום מתייחס לרשומות המוצגות בלבד.
        </p>
      )}
    </div>
  );
}

export default function PilotFeedbackPage() {
  const { user } = useAuth() || {};
  const canTriage = user?.role === "admin" || user?.role === "owner";
  const queryClient = useQueryClient();

  const [form, setForm] = useState(EMPTY_FORM);
  const [requestId, setRequestId] = useState(newRequestId);
  const [formError, setFormError] = useState(null);
  const [success, setSuccess] = useState(null);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["pilot-feedback"],
    queryFn: pilotFeedbackApi.list,
  });

  const createMutation = useMutation({
    mutationFn: (payload) => pilotFeedbackApi.create(payload),
    onSuccess: (record) => {
      setFormError(null);
      const highImpact = isHighImpact(record.severity);
      setSuccess(
        highImpact
          ? "הפידבק נרשם וסומן כבעל השפעה גבוהה — הוא ממתין לסיווג מפורש של מנהל או בעלים."
          : "הפידבק נרשם."
      );
      setForm(EMPTY_FORM);
      setRequestId(newRequestId());
      queryClient.invalidateQueries({ queryKey: ["pilot-feedback"] });
    },
    onError: (failure) => {
      setSuccess(null);
      setFormError(detailText(failure?.response?.data?.detail, "רישום הפידבק נכשל."));
    },
  });

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const submit = (event) => {
    event.preventDefault();
    setSuccess(null);
    const refusal = validateCapture(form);
    if (refusal) {
      setFormError(refusal);
      return;
    }
    setFormError(null);
    createMutation.mutate({
      severity: form.severity,
      workflow: form.workflow,
      reproducibility: form.reproducibility,
      ux_signal: form.ux_signal,
      title: form.title.trim(),
      observed: form.observed.trim(),
      expected: form.expected.trim() || null,
      evidence: {
        ticket_id: form.ticket_id.trim() || null,
        run_id: form.run_id.trim() || null,
        app_path: form.app_path.trim() || null,
      },
      client_request_id: requestId,
    });
  };

  const selectClass = `${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm`;
  const inputClass = `${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm`;
  const textareaClass = `${FOCUS_RING_CLS} mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm`;

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6" dir="rtl">
      <header className="mb-5">
        <p className="text-sm font-semibold text-brand-600">PRODUCT EXPERIENCE V2</p>
        <h1 className="mt-1 text-2xl font-bold text-gray-900 sm:text-3xl">פידבק פיילוט וסיווג עדיפויות</h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600">
          רשמו חיכוך אמיתי שנתקלתם בו בפיילוט. הרשומה כוללת חומרה, זרימת עבודה, מידת שחזור ואיתות
          חוויית משתמש, ומקשרת לראיה באמצעות מזהה קריאה/ריצה או נתיב פנימי — בלי להעתיק תוכן לקוח,
          סיסמאות או טוקנים. סיווג לתיקון פיתוח או לחוסם ידני הוא החלטה מפורשת של מנהל או בעלים;
          המערכת אינה מסווגת ואינה מתעדפת לבדה.
        </p>
      </header>

      <section aria-labelledby="capture-heading" className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h2 id="capture-heading" className="text-lg font-semibold text-gray-900">
          רישום פידבק
        </h2>
        <p className="mt-1 text-xs text-gray-600">
          אין נתוני דוגמה: כל שורה שתופיע בהמשך נרשמה על ידי משתמש אמיתי. שדות החומרה, הזרימה,
          השחזור והאיתות נדרשים כדי שאפשר יהיה לתעדף לפי חיכוך נמדד.
        </p>

        <form className="mt-4 space-y-4" onSubmit={submit} noValidate>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label htmlFor="pilot-severity" className="block text-xs font-medium text-gray-700">
                חומרה
              </label>
              <select id="pilot-severity" className={selectClass} value={form.severity} onChange={update("severity")}>
                <option value="">בחרו חומרה…</option>
                {SEVERITIES.map((value) => (
                  <option key={value} value={value}>
                    {severityMeta(value).label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="pilot-workflow" className="block text-xs font-medium text-gray-700">
                זרימת עבודה
              </label>
              <select id="pilot-workflow" className={selectClass} value={form.workflow} onChange={update("workflow")}>
                <option value="">בחרו זרימה…</option>
                {WORKFLOWS.map((value) => (
                  <option key={value} value={value}>
                    {workflowLabel(value)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="pilot-reproducibility" className="block text-xs font-medium text-gray-700">
                שחזור
              </label>
              <select
                id="pilot-reproducibility"
                className={selectClass}
                value={form.reproducibility}
                onChange={update("reproducibility")}
              >
                <option value="">בחרו מידת שחזור…</option>
                {REPRODUCIBILITIES.map((value) => (
                  <option key={value} value={value}>
                    {reproducibilityLabel(value)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="pilot-ux-signal" className="block text-xs font-medium text-gray-700">
                איתות חוויית משתמש
              </label>
              <select id="pilot-ux-signal" className={selectClass} value={form.ux_signal} onChange={update("ux_signal")}>
                <option value="">בחרו איתות…</option>
                {UX_SIGNALS.map((value) => (
                  <option key={value} value={value}>
                    {uxSignalLabel(value)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="pilot-title" className="block text-xs font-medium text-gray-700">
              כותרת
            </label>
            <input
              id="pilot-title"
              type="text"
              maxLength={140}
              className={inputClass}
              value={form.title}
              onChange={update("title")}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="pilot-observed" className="block text-xs font-medium text-gray-700">
                מה קרה בפועל
              </label>
              <textarea
                id="pilot-observed"
                rows={4}
                className={textareaClass}
                value={form.observed}
                onChange={update("observed")}
              />
            </div>
            <div>
              <label htmlFor="pilot-expected" className="block text-xs font-medium text-gray-700">
                מה ציפיתם שיקרה (אופציונלי)
              </label>
              <textarea
                id="pilot-expected"
                rows={4}
                className={textareaClass}
                value={form.expected}
                onChange={update("expected")}
              />
            </div>
          </div>

          <fieldset className="rounded-lg border border-gray-200 p-3">
            <legend className="px-1 text-xs font-medium text-gray-700">קישורי ראיה (בלי תוכן לקוח)</legend>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label htmlFor="pilot-ticket-id" className="block text-xs text-gray-600">
                  מזהה קריאה (UUID)
                </label>
                <input
                  id="pilot-ticket-id"
                  type="text"
                  dir="ltr"
                  className={inputClass}
                  value={form.ticket_id}
                  onChange={update("ticket_id")}
                />
              </div>
              <div>
                <label htmlFor="pilot-run-id" className="block text-xs text-gray-600">
                  מזהה ריצה (UUID)
                </label>
                <input
                  id="pilot-run-id"
                  type="text"
                  dir="ltr"
                  className={inputClass}
                  value={form.run_id}
                  onChange={update("run_id")}
                />
              </div>
              <div>
                <label htmlFor="pilot-app-path" className="block text-xs text-gray-600">
                  נתיב פנימי למסך
                </label>
                <input
                  id="pilot-app-path"
                  type="text"
                  dir="ltr"
                  placeholder="/tickets"
                  className={inputClass}
                  value={form.app_path}
                  onChange={update("app_path")}
                />
              </div>
            </div>
            <p className="mt-2 text-[11px] text-gray-500">
              אל תעתיקו לתיאור סיסמאות, טוקנים, מפתחות, תוכן הודעות או נתוני לקוח. השרת מסרב
              לטקסט שנראה כמו סוד, והקישור נפתח במסך שמחיל את הרשאות המשתמש.
            </p>
          </fieldset>

          {formError && (
            <p role="alert" className="text-sm text-red-700" data-testid="capture-error">
              {formError}
            </p>
          )}
          {success && (
            <p role="status" className="text-sm text-green-700" data-testid="capture-success">
              {success}
            </p>
          )}

          <button
            type="submit"
            disabled={createMutation.isPending}
            className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60`}
          >
            {createMutation.isPending ? "רושם…" : "רישום פידבק"}
          </button>
        </form>
      </section>

      <section aria-labelledby="findings-heading">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 id="findings-heading" className="text-lg font-semibold text-gray-900">
            פידבק שנרשם
          </h2>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 disabled:opacity-60`}
          >
            {isFetching ? "רענון…" : "רענון"}
          </button>
        </div>

        {isLoading && !data && <p className="text-sm text-gray-600">טוען פידבק פיילוט…</p>}

        {isError && !data && (
          <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            לא ניתן לטעון את הפידבק — מצב הרישום אינו ידוע. אין להסיק מכך שאין ממצאים.
          </div>
        )}

        {isError && data && (
          <p role="alert" className="mb-2 rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
            הרענון האחרון נכשל. הרשומות והמספרים שלהלן הם מהטעינה המוצלחת האחרונה.
          </p>
        )}

        {data && (
          <div className="space-y-3">
            <Summary data={data} />

            {data.items.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white p-5 text-sm text-gray-700">
                {data.unreadable > 0 ? (
                  <p>
                    לא נמצאו רשומות קריאות להצגה. {data.unreadable} רשומות שמורות אינן קריאות — אין
                    להסיק מהן שאין פידבק.
                  </p>
                ) : (
                  <p>לא נרשם עדיין פידבק פיילוט. הרשימה ריקה במכוון — אין כאן נתונים מומצאים.</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {data.items.map((item) => (
                  <FeedbackCard key={item.id} item={item} canTriage={canTriage} />
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
